import React, { ElementType, useEffect, useState } from 'react';
import { Box, Button, Icon, IconButton, Text, VStack, useToast } from '@chakra-ui/react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { RefreshCw, Settings } from 'lucide-react';
import { FaFacebook } from 'react-icons/fa';
import { getFacebookProfile } from '../../../hooks/facebook';
import { FacebookProfile } from '../../../utils/types';
import FacebookChatSidebar from '../pages/FacebookConversations';
import {
  FacebookInboxShell,
  FacebookSidebarHeader,
  FacebookThreadPlaceholder,
  fbAccent,
} from './facebookMessengerUi';

const FacebookMessengerLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { orgId } = useParams();
  const [facebookProfile, setFacebookProfile] = useState<FacebookProfile>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedConversationMatch = matchPath(
    '/organization/:orgId/inbox/facebook/conversations/:convId',
    location.pathname,
  );
  const selectedConversationId = selectedConversationMatch?.params?.convId;

  useEffect(() => {
    const fetchFacebookAccount = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        setError(null);
        const res = await getFacebookProfile(orgId);
        const data = res.data;

        if (!res.success) {
          setError('Failed to load your Facebook page.');
          return;
        }

        if (Array.isArray(data) && data.length > 0) {
          setFacebookProfile(data[0]);
        } else if (data && data.page_id) {
          setFacebookProfile(data);
        } else {
          setFacebookProfile(undefined);
        }
      } catch (err) {
        setError('Failed to load your Facebook page.');
        toast({
          title: 'Unable to load Facebook inbox',
          description: 'We could not load the connected page for this organization.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFacebookAccount();
  }, [orgId, refreshKey, toast]);

  if (!facebookProfile && !loading && !error) {
    return (
      <Box minH="100vh" bg="#f5f7fb" px={6} py={8}>
        <VStack
          maxW="520px"
          mx="auto"
          spacing={5}
          bg="white"
          borderRadius="28px"
          border="1px solid"
          borderColor="blackAlpha.100"
          boxShadow="0 24px 60px rgba(15,23,32,0.06)"
          px={8}
          py={10}
          textAlign="center"
        >
          <Icon as={FaFacebook as ElementType} boxSize={10} color={fbAccent} />
          <VStack spacing={2}>
            <Text fontSize="2xl" fontWeight="800" color="gray.900">
              Connect a Facebook Page
            </Text>
            <Text color="gray.600" lineHeight="1.7">
              Connect your page first to manage conversations, comments, and follow-up from one workspace.
            </Text>
          </VStack>
          <Button
            bg={fbAccent}
            color="white"
            _hover={{ bg: '#1667d9' }}
            onClick={() => navigate('/organization/inbox/facebook-messenger')}
            borderRadius="16px"
          >
            Connect Facebook Page
          </Button>
        </VStack>
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box minH="100vh" bg="#f5f7fb" px={6} py={8}>
        <VStack
          maxW="520px"
          mx="auto"
          spacing={4}
          bg="white"
          borderRadius="28px"
          border="1px solid"
          borderColor="blackAlpha.100"
          boxShadow="0 24px 60px rgba(15,23,32,0.06)"
          px={8}
          py={10}
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="800" color="gray.900">
            Facebook inbox unavailable
          </Text>
          <Text color="gray.600" lineHeight="1.7">
            {error}
          </Text>
          <Button
            bg={fbAccent}
            color="white"
            _hover={{ bg: '#1667d9' }}
            borderRadius="16px"
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            Retry
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <FacebookInboxShell
      hideSidebarOnMobile={!!selectedConversationId}
      sidebar={
        <>
          <FacebookSidebarHeader
            title="Messenger"
            subtitle={facebookProfile?.page_name || 'Facebook Page'}
            actions={
              <>
                <IconButton
                  aria-label="Refresh inbox"
                  icon={<Icon as={RefreshCw as ElementType} boxSize={4} />}
                  variant="ghost"
                  borderRadius="12px"
                  onClick={() => setRefreshKey((value) => value + 1)}
                />
                {facebookProfile ? (
                  <IconButton
                    aria-label="Page settings"
                    icon={<Icon as={Settings as ElementType} boxSize={4} />}
                    variant="ghost"
                    borderRadius="12px"
                    onClick={() =>
                      navigate(
                        `/organization/${orgId}/inbox/facebook/${facebookProfile.id}/settings`,
                      )
                    }
                  />
                ) : null}
              </>
            }
          />
          {facebookProfile && orgId ? (
            <FacebookChatSidebar
              key={`${facebookProfile.id}-${refreshKey}`}
              orgId={orgId}
              facebookAccId={facebookProfile.id}
              selectedConversationId={selectedConversationId}
            />
          ) : null}
        </>
      }
    >
      {selectedConversationId ? (
        children
      ) : (
        <FacebookThreadPlaceholder />
      )}
    </FacebookInboxShell>
  );
};

export default FacebookMessengerLayout;
